import React, { useEffect, useState, useRef } from 'react';
import { ArrowRight, Sparkles, TrendingUp, Target, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import GrowthCurve from '../components/GrowthCurve';
import HeroParticles from '../components/HeroParticles';
import CountUp from '../components/CountUp';
import { mockServices, mockClients, mockPortfolio, mockFAQs } from '../mock';

const STAT_CLASS = "font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]";

const ScrollReveal = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), delay);
        }
      },
      { threshold: 0.1 }
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [delay]);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      {children}
    </div>
  );
};

const Home = () => {
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        <HeroParticles />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 relative z-10">
          <div className="text-center space-y-8">
            {/* Status Badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full" style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)' }}>
              <div className="w-2 h-2 bg-[#11AAB6] rounded-full animate-pulse" />
              <span className="font-['Inter'] text-sm text-[#11AAB6] font-medium">Growth & Automation Studio</span>
            </div>

            {/* Main Headline */}
            <h1 className="font-['Space_Grotesk'] font-bold text-5xl md:text-7xl lg:text-8xl text-[#052E6C] leading-tight">
              Your Growth,
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]">Automated.</span>
            </h1>

            <p className="font-['Inter'] text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
              Transform your business with intelligent automation and data-driven marketing strategies that work 24/7.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button 
                asChild
                className="bg-[#11AAB6] hover:bg-[#0E8A94] text-white px-8 py-6 text-lg rounded-full font-['Inter'] font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
              >
                <a href="https://booking.impactechdigital.com" target="_blank" rel="noopener noreferrer">
                  Book Free Consultation
                  <ArrowRight className="ml-2 inline-block group-hover:translate-x-1 transition-transform" size={20} />
                </a>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-[#11AAB6] text-[#11AAB6] hover:text-[#052E6C] px-8 py-6 text-lg rounded-full font-['Inter'] font-medium transition-all duration-300 hover:scale-105"
                style={{ 
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(17, 170, 182, 0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <Link to="/services">
                  Explore Services
                </Link>
              </Button>
            </div>
          </div>

          {/* Growth Curve Animation */}
          <div className="mt-16">
            <GrowthCurve />
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="text-[#11AAB6]" size={32} />
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-16" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)', borderTop: '1px solid rgba(5, 46, 108, 0.12)', borderBottom: '1px solid rgba(5, 46, 108, 0.12)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <CountUp value="150+" className={STAT_CLASS} />
              <p className="font-['Inter'] mt-2" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>Happy Clients</p>
            </div>
            <div className="text-center">
              <CountUp value="8+" className={STAT_CLASS} />
              <p className="font-['Inter'] mt-2" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>Years Experience</p>
            </div>
            <div className="text-center">
              <CountUp value="312%" className={STAT_CLASS} />
              <p className="font-['Inter'] mt-2" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>Avg Lead Growth</p>
            </div>
            <div className="text-center">
              <CountUp value="98%" className={STAT_CLASS} />
              <p className="font-['Inter'] mt-2" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>Satisfaction Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Marquee */}
      <section className="py-12 overflow-hidden bg-white">
        <div className="text-center mb-8 px-6">
          <p className="font-['Inter'] text-sm font-semibold uppercase tracking-[0.2em] text-[#11AAB6] mb-2" data-testid="clients-section-label">
            Our Clients
          </p>
          <h2 className="font-['Space_Grotesk'] font-bold text-2xl md:text-3xl text-[#052E6C]">
            Trusted by growing brands
          </h2>
        </div>
        <div className="relative">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...mockClients, ...mockClients].map((client, index) => (
              <span
                key={index}
                className="font-['Inter'] text-lg font-medium mx-8 hover:text-[#11AAB6] transition-colors cursor-default"
                style={{ color: 'rgba(5, 46, 108, 0.55)' }}
              >
                {client}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Trust Us */}
      <ScrollReveal>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-[#052E6C] mb-4">
                Why businesses trust us
              </h2>
              <p className="font-['Inter'] text-lg max-w-2xl mx-auto" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                We don't just deliver campaigns — we build systems that scale your growth.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Target,
                  title: 'Tailored Solutions',
                  description: 'Every business is unique. We craft custom strategies that align with your specific goals, audience, and industry challenges.'
                },
                {
                  icon: TrendingUp,
                  title: 'Results-Driven',
                  description: 'We focus on what matters — measurable growth. Our data-driven approach ensures every dirham delivers maximum ROI.'
                },
                {
                  icon: Sparkles,
                  title: '24/7 Support',
                  description: 'Your success doesn\'t sleep. Neither do our automated systems. Get continuous optimization and responsive support whenever you need it.'
                }
              ].map((feature, index) => (
                <Card 
                  key={index}
                  className="group transition-all duration-300 hover:shadow-xl cursor-pointer overflow-hidden relative"
                  style={{ 
                    border: '1px solid rgba(5, 46, 108, 0.12)'
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setCursorPosition({
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top
                    });
                  }}
                >
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle 200px at ${cursorPosition.x}px ${cursorPosition.y}px, rgba(17, 170, 182, 0.1), transparent)`
                    }}
                  />
                  <CardContent className="p-8 relative z-10">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300"
                      style={{ backgroundColor: 'rgba(17, 170, 182, 0.12)' }}
                    >
                      <feature.icon className="text-[#11AAB6]" size={28} />
                    </div>
                    <h3 className="font-['Space_Grotesk'] font-bold text-xl text-[#052E6C] mb-3">
                      {feature.title}
                    </h3>
                    <p className="font-['Inter'] leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Services Preview */}
      <ScrollReveal delay={200}>
        <section className="py-24" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)' }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-[#052E6C] mb-4">
                20+ services, one mission
              </h2>
              <p className="font-['Inter'] text-lg max-w-2xl mx-auto" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                From strategy to execution, we handle everything your business needs to thrive online.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {mockServices.slice(0, 3).map((service) => (
                <Card 
                  key={service.id} 
                  className="group transition-all duration-300 hover:shadow-lg cursor-pointer"
                  style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <span className="font-['Space_Grotesk'] font-bold text-3xl text-transparent bg-clip-text bg-gradient-to-r from-[#11AAB6] to-[#052E6C]">
                        {String(service.id).padStart(2, '0')}
                      </span>
                      <ArrowRight className="text-[#11AAB6] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" size={20} />
                    </div>
                    <h3 className="font-['Space_Grotesk'] font-bold text-lg text-[#052E6C] mb-2">
                      {service.title}
                    </h3>
                    <p className="font-['Inter'] text-sm" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button 
                asChild
                className="bg-[#11AAB6] hover:bg-[#0E8A94] text-white px-8 py-6 text-lg rounded-full font-['Inter'] font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
              >
                <Link to="/services">
                  View All Services
                  <ArrowRight className="ml-2 inline-block group-hover:translate-x-1 transition-transform" size={20} />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Portfolio */}
      <ScrollReveal delay={300}>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-[#052E6C] mb-4">
                Success stories
              </h2>
              <p className="font-['Inter'] text-lg max-w-2xl mx-auto" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                Real results from real businesses we've helped transform.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {mockPortfolio.map((project) => (
                <div 
                  key={project.id} 
                  className="group relative overflow-hidden rounded-3xl cursor-pointer h-80"
                >
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#052E6C] via-[#052E6C]/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <span className="font-['Inter'] text-sm mb-2 block" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{project.category}</span>
                    <h3 className="font-['Space_Grotesk'] font-bold text-2xl mb-2">{project.title}</h3>
                    <p className="font-['Inter'] font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{project.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* FAQ */}
      <ScrollReveal delay={400}>
        <section className="py-24" style={{ backgroundColor: 'rgba(17, 170, 182, 0.05)' }}>
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-[#052E6C] mb-4">
                Frequently asked questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {mockFAQs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-2xl px-6 hover:shadow-md transition-shadow"
                  style={{ border: '1px solid rgba(5, 46, 108, 0.12)' }}
                >
                  <AccordionTrigger className="font-['Space_Grotesk'] font-semibold text-[#052E6C] hover:text-[#11AAB6] text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="font-['Inter'] leading-relaxed" style={{ color: 'rgba(5, 46, 108, 0.7)' }}>
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA Band */}
      <section className="bg-[#052E6C] py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-['Space_Grotesk'] font-bold text-4xl md:text-5xl text-white mb-6">
            Ready to automate your growth?
          </h2>
          <p className="font-['Inter'] text-lg mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Join 150+ businesses that trust ImpacTech to scale their success. Book a free consultation today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              asChild
              className="bg-white text-[#052E6C] hover:bg-white/90 px-8 py-6 text-lg rounded-full font-['Inter'] font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
            >
              <a href="https://booking.impactechdigital.com" target="_blank" rel="noopener noreferrer">
                Book Free Call
                <ArrowRight className="ml-2 inline-block group-hover:translate-x-1 transition-transform" size={20} />
              </a>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white text-white px-8 py-6 text-lg rounded-full font-['Inter'] font-medium transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Link to="/contact">
                Get in Touch
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }

        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
